import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { Subject } from 'rxjs';
import { CoresService } from './cores.service';
import {
  computeBuildingFootprintExtentMeters,
  computeThreeBuildingMatrixFromAnchor,
  resolveBuildingLocalCenter,
} from '../utils/building-georef.util';

/** Host context supplied by ThreeIntegrationService for GLB/Babylon overlay work. */
export interface ThreeBuildingHost {
  getThreeScene(): THREE.Scene | null;
  getThreeCamera(): THREE.PerspectiveCamera | null;
  getThreeRenderer(): THREE.WebGLRenderer | null;
  getControls(): OrbitControls | null;
  getCesiumThreeContainer(): HTMLElement | null;
  getBuildingGeographicCenter(): { lat: number; lon: number } | null;
  setBuildingGeographicCenter(center: { lat: number; lon: number } | null): void;
  getBuildingModels(): Map<string, THREE.Group>;
  getBuildingEntities(): Map<string, any>;
  getBuildingContainerGroup(): THREE.Group | null;
  getAreaMapEntities(): Map<string, any>;
  isThreeJSActive(): boolean;
  setThreeJSActive(active: boolean): void;
  getMouse(): THREE.Vector2;
  getRaycaster(): THREE.Raycaster;
  reinitializeThreeJSIntegration(container: HTMLElement): void;
  startRenderLoop(): void;
  synchronizeCameras(): void;
  resizeThreeJSRenderer(): void;
  getPreviouslyHighlightedFloor(): string | null;
  setPreviouslyHighlightedFloor(floorId: string | null): void;
  emitFloorClick(floorId: string, floorNumberHint?: number): void;
  focusFloorOnArea(areaId: string, container: HTMLElement): void;
  highlightFloorById(floorId: string, highlight: boolean, syncBabylon: boolean): void;
}

export interface GlbFloorGroup {
  floorId: string;
  floorNumber: number;
  minY: number;
  maxY: number;
  meshes: THREE.Mesh[];
}

@Injectable({
  providedIn: 'root',
})
export class BlenderBuildingService {
  private host: ThreeBuildingHost | null = null;

  private glbMapRootGroup: THREE.Group | null = null;
  private glbMapOverlayActive = false;
  private glbMapAttachedModels: THREE.Object3D[] = [];
  private glbMapHiddenNodes: THREE.Object3D[] = [];
  private campusGlbBuildingMap = new Map<string, {
    perimeterId: string;
    building: any;
    model: THREE.Object3D | null;
    wireGroup: THREE.Group | null;
    isGlbActive: boolean;
  }>();
  private dracoLoader: DRACOLoader | null = null;
  private glbMapHighlightedMeshes = new Map<THREE.Mesh, THREE.Material>();
  private glbMapHighlightMaterialCache = new Map<THREE.Mesh, THREE.Material>();
  private glbMapOriginalMaterials = new Map<THREE.Mesh, THREE.Material>();
  private glbMapHighlightedFloorId: string | null = null;
  private glbMapFloorCatalog = new Map<
    string,
    { floorNumber?: number; perimeterId?: string; meshes: THREE.Mesh[] }
  >();
  static readonly FLOOR_HIGHLIGHT_ENABLED = false;
  private static readonly GLB_FLOOR_HIGHLIGHT_EMISSIVE = 0x000000;
  private static readonly GLB_FLOOR_HIGHLIGHT_INTENSITY = 0;
  private cameraChangedListener?: () => void;

  private lastBabylonHighlightToken: string | null = null;
  private alertHighlightedFloors: Set<string> = new Set();
  private babylonViewerRef: unknown = null;

  private wireframeReadySubject = new Subject<void>();
  public wireframeReady$ = this.wireframeReadySubject.asObservable();
  private floorHighlightSubject = new Subject<{ floorId: string; highlight: boolean }>();
  public floorHighlight$ = this.floorHighlightSubject.asObservable();
  private alertFloorHighlightSubject = new Subject<{ floorIds: string[] }>();
  public alertFloorHighlight$ = this.alertFloorHighlightSubject.asObservable();
  private roomHighlightSubject = new Subject<{ roomIds: string[]; buildingIds?: string[] }>();
  public roomHighlight$ = this.roomHighlightSubject.asObservable();

  constructor(private coresService: CoresService) { }

  setHost(host: ThreeBuildingHost): void {
    this.host = host;
  }

  private h(): ThreeBuildingHost {
    if (!this.host) {
      throw new Error('BlenderBuildingService: host not attached');
    }
    return this.host;
  }

  notifyWireframeReady(): void {
    this.wireframeReadySubject.next();
  }

  isGlbMapOverlayActive(): boolean {
    return this.glbMapOverlayActive;
  }

  private getCampusBuildingGlbEntry(buildingOrId: any): {
    perimeterId: string;
    building: any;
    model: THREE.Object3D | null;
    wireGroup: THREE.Group | null;
    isGlbActive: boolean;
  } | null {
    if (!buildingOrId) return null;
    if (typeof buildingOrId === 'string' || typeof buildingOrId === 'number') {
      return this.campusGlbBuildingMap.get(String(buildingOrId)) ?? null;
    }
    const keys = [
      buildingOrId?.perimeterId,
      buildingOrId?.id,
      buildingOrId?.arcAngelId,
      buildingOrId?.object?.id,
    ].filter((k) => k != null && k !== '').map((k) => String(k));

    for (const k of keys) {
      const found = this.campusGlbBuildingMap.get(k);
      if (found) return found;
    }
    return null;
  }

  private registerCampusGlbBuilding(building: any, model: THREE.Object3D | null, wireGroup: THREE.Group | null): void {
    if (!building) return;
    const existing = this.getCampusBuildingGlbEntry(building);
    const keys = [
      building?.perimeterId,
      building?.id,
      building?.arcAngelId,
      building?.object?.id,
    ].filter((k) => k != null && k !== '').map((k) => String(k));

    const record = existing || {
      perimeterId: String(building?.perimeterId ?? building?.id ?? ''),
      building,
      model,
      wireGroup,
      isGlbActive: true,
    };

    if (model) record.model = model;
    if (wireGroup) record.wireGroup = wireGroup;
    if (model && !record.isGlbActive) {
      model.visible = false;
    }

    keys.forEach((k) => this.campusGlbBuildingMap.set(k, record));
  }

  hasCampusGlbBuilding(buildingOrId: any): boolean {
    return this.getCampusBuildingGlbEntry(buildingOrId) !== null;
  }

  isCampusBuildingGlbActive(buildingOrId: any): boolean {
    const record = this.getCampusBuildingGlbEntry(buildingOrId);
    if (record) {
      return record.isGlbActive;
    }
    return this.glbMapOverlayActive;
  }

  toggleCampusBuildingGlb(buildingOrId: any): boolean {
    const record = this.getCampusBuildingGlbEntry(buildingOrId);
    if (!record) {
      return false;
    }

    if (!record.wireGroup) {
      record.wireGroup = this.findBuildingWireframeGroup(record.building);
    }

    const newGlbActive = !record.isGlbActive;
    record.isGlbActive = newGlbActive;

    // 1. Toggle GLB model
    if (record.model) {
      record.model.visible = newGlbActive;
    }

    // 2. Toggle Wireframe
    const containerGroup = this.h().getBuildingContainerGroup();
    if (containerGroup) {
      const allRecords = Array.from(new Set(this.campusGlbBuildingMap.values()));
      const anyWireframeVisible = allRecords.some((r) => !r.isGlbActive);
      containerGroup.visible = anyWireframeVisible;

      allRecords.forEach((r) => {
        if (!r.wireGroup) {
          r.wireGroup = this.findBuildingWireframeGroup(r.building);
        }
        if (r.wireGroup) {
          r.wireGroup.visible = !r.isGlbActive;
        }
      });
    }

    // 3. Toggle Cesium Entity
    if (record.building?.cesiumEntity) {
      record.building.cesiumEntity.show = !newGlbActive;
    }

    this.renderMapOverlayFrame();
    this.coresService.getViewer()?.scene.requestRender();
    return true;
  }


  applyFloorHighlightSync(floorId: string, highlight: boolean, syncBabylon: boolean): void {
    const rawFloorKey = String(floorId ?? '').trim();
    if (!rawFloorKey) {
      return;
    }
    if (highlight && !BlenderBuildingService.FLOOR_HIGHLIGHT_ENABLED) {
      return;
    }

    if (
      highlight &&
      this.h().getPreviouslyHighlightedFloor() &&
      this.floorHighlightKeysEquivalent(this.h().getPreviouslyHighlightedFloor(), rawFloorKey)
    ) {
      return;
    }

    const resolvedFloor = this.resolveFloorAreaEntity(rawFloorKey);

    if (
      this.h().getPreviouslyHighlightedFloor() &&
      !this.floorHighlightKeysEquivalent(this.h().getPreviouslyHighlightedFloor(), rawFloorKey)
    ) {
      const prevFloorId = String(this.h().getPreviouslyHighlightedFloor()).trim();
      const prevResolved = this.resolveFloorAreaEntity(prevFloorId);
      if (prevResolved || prevFloorId) {
        this.h().highlightFloorById(prevFloorId, false, syncBabylon);
      }
    }

    const babylonToken = this.toBabylonViewerHighlightId(rawFloorKey, resolvedFloor);

    if (highlight) {
      this.h().setPreviouslyHighlightedFloor(resolvedFloor?.id || resolvedFloor?.perimeterId || rawFloorKey);
      if (syncBabylon && this.babylonViewerRef) {
        this.floorHighlightSubject.next({ floorId: babylonToken, highlight: true });
        this.lastBabylonHighlightToken = babylonToken;
      }
      if (this.glbMapOverlayActive) {
        this.highlightGlbMapFloor(rawFloorKey, true, resolvedFloor);
      }
    }

    if (!highlight) {
      if (syncBabylon && this.babylonViewerRef) {
        const clearResolved = resolvedFloor ?? this.resolveFloorAreaEntity(rawFloorKey);
        const clearTok = this.toBabylonViewerHighlightId(rawFloorKey, clearResolved);
        this.floorHighlightSubject.next({ floorId: clearTok, highlight: false });
      }
      if (
        this.glbMapOverlayActive &&
        this.glbMapHighlightedFloorId &&
        (this.glbFloorStackKeysEquivalent(this.glbMapHighlightedFloorId, babylonToken) ||
          this.floorHighlightKeysEquivalent(this.glbMapHighlightedFloorId, rawFloorKey))
      ) {
        this.clearGlbMapFloorHighlight();
      }
      this.h().setPreviouslyHighlightedFloor(null);
      this.lastBabylonHighlightToken = null;
    }

  }

  handleGlbMapClick(event: MouseEvent): boolean {
    if (!this.glbMapOverlayActive) {
      return false;
    }
    const rect = this.getGlbMapPointerRect();
    if (!rect?.width || !rect.height) {
      return false;
    }
    this.setMouseFromEvent(event, rect);
    const intersects = this.raycastGlbMapObjects();
    if (intersects.length > 0) {
      const glbPick = this.resolveGlbMapFloorPick(intersects[0].object);
      if (glbPick) {
        this.handleGlbMapFloorClick(glbPick.floorId, glbPick.floorNumberHint);
      }
    }
    return true;
  }

  registerBabylonViewer(component: unknown): void {
    this.babylonViewerRef = component;
  }

  onBabylonFloorClick(floorId: string, _mesh: unknown, floorNumberHint?: number): void {
    if (!floorId) {
      return;
    }
    const resolved = this.resolveFloorAreaEntity(floorId);
    const nextTok = this.toBabylonViewerHighlightId(floorId, resolved ?? undefined);
    if (this.lastBabylonHighlightToken === nextTok) {
      return;
    }
    this.h().highlightFloorById(floorId, true, false);
    const container = this.h().getCesiumThreeContainer();
    if (container) {
      this.h().focusFloorOnArea(floorId, container);
    }
    this.h().emitFloorClick(floorId, floorNumberHint);
  }
  /** Campus anchor used for Three.js camera sync and building ENU placement. */
  getBuildingGeographicCenter(): { lat: number; lon: number } | null {
    const center = this.h().getBuildingGeographicCenter();
    if (!center) {
      return null;
    }
    return { ...center };
  }

  /** Hide/show the Three.js canvas without tearing down the scene (e.g. while GLB viewer is active). */
  setOverlayVisible(visible: boolean): void {
    const canvas = this.h().getThreeRenderer()?.domElement;
    if (!canvas) {
      return;
    }
    if (visible) {
      canvas.style.display = 'block';
      canvas.style.visibility = 'visible';
      canvas.style.opacity = '1';
      canvas.style.pointerEvents = 'none';
      if (this.h().getCesiumThreeContainer()) {
        this.h().getCesiumThreeContainer()!.appendChild(canvas);
        this.h().resizeThreeJSRenderer();
      }
    } else {
      canvas.style.display = 'none';
    }
  }

  /**
   * Initialize the map overlay for georeferenced GLB without rebuilding wireframe meshes.
   * Preserves the existing geographic center when wireframe view is already active so
   * the Cesium camera does not appear to jump/zoom when switching to GLB.
   */
  prepareGlbMapOverlayContext(
    anchor?: { lat: number; lon: number },
    options?: { forceAnchor?: boolean }
  ): boolean {
    const cesiumContainer = document.getElementById('cesiumContainer');
    if (!cesiumContainer) {
      console.error('ThreeIntegrationService: cesiumContainer not found');
      return false;
    }

    if (!this.h().getThreeScene() || this.h().getCesiumThreeContainer() !== cesiumContainer) {
      this.h().reinitializeThreeJSIntegration(cesiumContainer);
    }

    const existingCenter = this.h().getBuildingGeographicCenter();
    const preserveCenter =
      !options?.forceAnchor &&
      this.h().isThreeJSActive() &&
      existingCenter &&
      typeof existingCenter.lat === 'number' &&
      typeof existingCenter.lon === 'number';

    if (!preserveCenter) {
      if (!anchor) {
        console.error('ThreeIntegrationService: anchor required for new GLB overlay');
        return false;
      }
      this.h().setBuildingGeographicCenter({ lat: anchor.lat, lon: anchor.lon });
    }

    this.h().setThreeJSActive(true);
    this.hideWireframeForGlbOverlay();

    const renderer = this.h().getThreeRenderer();
    const threeContainer = this.h().getCesiumThreeContainer();
    if (renderer && threeContainer) {
      renderer.domElement.style.display = '';
      this.h().resizeThreeJSRenderer();
    }

    this.h().startRenderLoop();
    this.h().synchronizeCameras();
    return !!this.h().getThreeScene();
  }

  setGlbMapGeographicCenter(anchor: { lat: number; lon: number }): void {
    this.h().setBuildingGeographicCenter({ lat: anchor.lat, lon: anchor.lon });
    this.h().synchronizeCameras();
  }

  hideWireframeForGlbOverlay(): void {
    const controls = this.h().getControls();
    if (controls) {
      controls.enabled = false;
    }
    const containerGroup = this.h().getBuildingContainerGroup();
    if (containerGroup) {
      containerGroup.visible = false;
    }
  }

  /** GLTFLoader with Draco for compressed GLBs from export pipeline. */
  private createGltfLoader(): GLTFLoader {
    const loader = new GLTFLoader();
    loader.setCrossOrigin('anonymous');
    if (!this.dracoLoader) {
      this.dracoLoader = new DRACOLoader();
      this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
      this.dracoLoader.preload();
    }
    loader.setDRACOLoader(this.dracoLoader);
    return loader;
  }

  async showGlbMapOverlay(glbUrl: string, building: any): Promise<boolean> {
    if (!glbUrl || !building || !this.h().getThreeScene()) {
      return false;
    }

    this.hideGlbMapOverlay(false);

    try {
      const loader = this.createGltfLoader();
      const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
        loader.load(glbUrl, resolve, undefined, reject);
      });

      this.prepareGlbMeshesForMap(gltf.scene);
      const wireGroup = this.findBuildingWireframeGroup(building);
      const modelRoot = this.fitGlbModelForMap(gltf.scene, building, wireGroup, 'pin-origin');

      this.glbMapRootGroup = new THREE.Group();
      this.glbMapRootGroup.name = 'GlbMapRoot';

      if (wireGroup) {
        wireGroup.updateMatrixWorld(true);
        const wrapper = new THREE.Group();
        wrapper.matrix.copy(wireGroup.matrixWorld);
        wrapper.matrixAutoUpdate = false;
        wrapper.add(modelRoot);
        this.glbMapRootGroup.add(wrapper);
        this.glbMapAttachedModels.push(wrapper);
      } else {
        this.glbMapRootGroup.add(modelRoot);
      }

      const containerGroup = this.h().getBuildingContainerGroup();
      if (containerGroup) {
        containerGroup.visible = false;
      }
      this.h().getThreeScene()?.add(this.glbMapRootGroup);

      const controls = this.h().getControls();
      if (controls) {
        controls.enabled = false;
      }

      this.glbMapOverlayActive = true;
      this.setOverlayVisible(true);
      this.bindCesiumCameraSync();
      this.h().startRenderLoop();
      this.h().synchronizeCameras();
      this.renderMapOverlayFrame();
      await this.animateBuildingFloorsProgressively(modelRoot, building, String(building?.perimeterId ?? building?.id ?? ''));
      this.rebuildGlbMapFloorCatalog();
      return true;
    } catch (err) {
      console.error('ThreeIntegrationService: GLB map overlay failed', err);
      const containerGroup = this.h().getBuildingContainerGroup();
      if (containerGroup) {
        containerGroup.visible = true;
      }
      return false;
    }
  }

  async showCampusGlbMapOverlay(
    entries: Array<{ glbUrl: string; building: any }>,
    onProgress?: (
      perimeterId: string,
      status: 'loading' | 'building' | 'done' | 'error',
      currentFloor: number,
      totalFloors: number,
      percent: number
    ) => void
  ): Promise<boolean> {
    if (!this.h().getThreeScene() || !entries.length) {
      return false;
    }

    this.hideGlbMapOverlay(false);
    this.clearGlbMapAttachedModels();
    this.glbMapRootGroup = new THREE.Group();
    this.glbMapRootGroup.name = 'GlbMapRoot';
    let attachedToWireframe = false;

    const scene = this.h().getThreeScene();
    if (scene) {
      scene.add(this.glbMapRootGroup);
    }

    this.glbMapOverlayActive = true;
    this.setOverlayVisible(true);
    this.bindCesiumCameraSync();
    this.h().startRenderLoop();
    this.h().synchronizeCameras();

    // Enable local clipping on renderer
    const renderer = this.h().getThreeRenderer();
    if (renderer) {
      renderer.localClippingEnabled = true;
    }

    const loader = this.createGltfLoader();
    this.campusGlbBuildingMap.clear();
    entries.forEach((e) => {
      if (e?.building) {
        this.registerCampusGlbBuilding(e.building, null, this.findBuildingWireframeGroup(e.building));
      }
    });

    // 1. Fetch & prepare all campus GLB building models in parallel
    const preparePromises = entries.map(async (entry) => {
      if (!entry?.glbUrl || !entry?.building) return null;
      const perimeterId = String(entry.building?.perimeterId ?? entry.building?.id ?? '');
      onProgress?.(perimeterId, 'loading', 0, 1, 10);

      try {
        const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) => {
          loader.load(
            entry.glbUrl,
            (gltfData: any) => resolve(gltfData),
            (evt: any) => {
              if (evt.total > 0) {
                // Map network download phase smoothly from 10% to 40%
                const p = Math.min(40, 10 + Math.round((evt.loaded / evt.total) * 30));
                onProgress?.(perimeterId, 'loading', 0, 1, p);
              }
            },
            reject
          );
        });

        this.prepareGlbMeshesForMap(gltf.scene);

        const wireGroup = this.findBuildingWireframeGroup(entry.building);
        let alignMode: 'footprint-center' | 'pin-origin' = 'footprint-center';
        if (wireGroup) {
          const previewBox = this.getWireframeLocalBoundingBox(wireGroup);
          const previewCenter = previewBox.getCenter(new THREE.Vector3());
          if (Math.hypot(previewCenter.x, previewCenter.z) > 10) {
            alignMode = 'pin-origin';
          }
        }
        const modelRoot = this.fitGlbModelForMap(gltf.scene, entry.building, wireGroup, alignMode);
        let attachedModel: THREE.Object3D;
        if (wireGroup && this.glbMapRootGroup) {
          wireGroup.updateMatrixWorld(true);
          const wrapper = new THREE.Group();
          wrapper.matrix.copy(wireGroup.matrixWorld);
          wrapper.matrixAutoUpdate = false;
          wrapper.add(modelRoot);
          this.glbMapRootGroup.add(wrapper);
          this.glbMapAttachedModels.push(wrapper);
          attachedModel = wrapper;
        } else if (this.glbMapRootGroup) {
          this.glbMapRootGroup.add(modelRoot);
          attachedModel = modelRoot;
        } else {
          attachedModel = modelRoot;
        }

        this.registerCampusGlbBuilding(entry.building, attachedModel, wireGroup);

        modelRoot.updateMatrixWorld(true);

        const sortedFloorGroups = this.extractSortedGlbFloorGroups(gltf.scene, entry.building);

        // Hide ALL meshes initially before reveal animation starts
        gltf.scene.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.visible = false;
          }
        });

        return {
          modelRoot,
          building: entry.building,
          perimeterId,
          floorGroups: sortedFloorGroups,
        };
      } catch (err) {
        console.error(`Campus building model load failed for ${perimeterId}:`, err);
        onProgress?.(perimeterId, 'error', 0, 1, 0);
        return null;
      }
    });

    const preparedResults = (await Promise.all(preparePromises)).filter(
      (res): res is { modelRoot: THREE.Object3D; building: any; perimeterId: string; floorGroups: GlbFloorGroup[] } => res !== null
    );

    // 2. Reveal all campus building floors SIMULTANEOUSLY from lowest floor (Floor 1) to highest floor in perfect sync!
    const maxFloorsCount = Math.max(...preparedResults.map((item) => item.floorGroups.length), 1);
    const STEP_DELAY_MS = 35; // 35ms per floor step for fast, instant building construction!

    for (let stepIndex = 0; stepIndex < maxFloorsCount; stepIndex++) {
      preparedResults.forEach((item) => {
        const record = this.getCampusBuildingGlbEntry(item.building);
        if (record && !record.isGlbActive) {
          return;
        }
        const totalFloors = item.floorGroups.length;
        if (stepIndex < totalFloors) {
          const currentGroup = item.floorGroups[stepIndex];
          currentGroup.meshes.forEach((mesh) => {
            if (!mesh.userData['isFilteredAsset']) {
              mesh.visible = true;
            }
          });

          const displayFloorNumber = currentGroup.floorNumber ?? (stepIndex + 1);
          // Map floor reveal animation smoothly from 40% to 100%
          const percent = Math.min(100, 40 + Math.round(((stepIndex + 1) / totalFloors) * 60));
          onProgress?.(item.perimeterId, 'building', displayFloorNumber, totalFloors, percent);
        }
      });

      this.renderMapOverlayFrame();
      await new Promise((r) => setTimeout(r, STEP_DELAY_MS));
    }

    // Ensure structural meshes are visible at completion while keeping filtered assets hidden
    preparedResults.forEach((item) => {
      const record = this.getCampusBuildingGlbEntry(item.building);
      if (record && !record.isGlbActive) {
        if (record.model) record.model.visible = false;
        return;
      }
      item.modelRoot.traverse((child) => {
        if (child instanceof THREE.Mesh && !child.userData['isFilteredAsset']) {
          child.visible = true;
        }
      });
      const totalFloors = item.floorGroups.length;
      onProgress?.(item.perimeterId, 'done', totalFloors, totalFloors, 100);
    });

    if (!this.glbMapAttachedModels.length && (!this.glbMapRootGroup || !this.glbMapRootGroup.children.length)) {
      if (this.glbMapRootGroup && scene) {
        scene.remove(this.glbMapRootGroup);
      }
      this.glbMapRootGroup = null;
      return false;
    }

    const containerGroup = this.h().getBuildingContainerGroup();
    if (containerGroup) {
      const allRecords = Array.from(new Set(this.campusGlbBuildingMap.values()));
      const anyWireframeVisible = allRecords.some((r) => !r.isGlbActive);
      containerGroup.visible = anyWireframeVisible;
      allRecords.forEach((r) => {
        if (!r.wireGroup) {
          r.wireGroup = this.findBuildingWireframeGroup(r.building);
        }
        if (r.wireGroup) {
          r.wireGroup.visible = !r.isGlbActive;
        }
      });
    }

    const campusControls = this.h().getControls();
    if (campusControls) {
      campusControls.enabled = false;
    }
    this.renderMapOverlayFrame();
    this.rebuildGlbMapFloorCatalog();
    return true;
  }

  /**
   * Progressively animates building construction strictly bottom-to-top (floor 0, 1, 2, 3, 4, 5, 6...)
   * using GPU height clipping planes to guarantee 100% monotonic ground-to-roof reveal.
   */
  private async animateBuildingFloorsProgressively(
    modelRoot: THREE.Object3D,
    building: any,
    perimeterId: string,
    onProgress?: (
      perimeterId: string,
      status: 'loading' | 'building' | 'done' | 'error',
      currentFloor: number,
      totalFloors: number,
      percent: number
    ) => void
  ): Promise<void> {
    modelRoot.updateMatrixWorld(true);
    const sortedFloorGroups = this.extractSortedGlbFloorGroups(modelRoot, building);

    if (!sortedFloorGroups.length) {
      onProgress?.(perimeterId, 'done', 1, 1, 100);
      return;
    }

    // Hide all meshes initially before reveal animation starts
    modelRoot.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.visible = false;
      }
    });

    const totalFloorsCount = sortedFloorGroups.length;
    const durationPerStepMs = 120; // 120ms per floor for clear, responsive reveal

    for (let stepIndex = 0; stepIndex < totalFloorsCount; stepIndex++) {
      const currentGroup = sortedFloorGroups[stepIndex];
      currentGroup.meshes.forEach((mesh) => {
        if (!mesh.userData['isFilteredAsset']) {
          mesh.visible = true;
        }
      });

      const displayFloorNumber = currentGroup.floorNumber ?? (stepIndex + 1);
      const progressPercent = Math.min(100, 40 + Math.round(((stepIndex + 1) / totalFloorsCount) * 60));
      onProgress?.(perimeterId, 'building', displayFloorNumber, totalFloorsCount, progressPercent);

      this.renderMapOverlayFrame();
      await new Promise((r) => setTimeout(r, durationPerStepMs));
    }

    // Ensure structural meshes are visible at completion while keeping filtered assets hidden
    modelRoot.traverse((child) => {
      if (child instanceof THREE.Mesh && !child.userData['isFilteredAsset']) {
        child.visible = true;
      }
    });

    this.renderMapOverlayFrame();
    onProgress?.(perimeterId, 'done', totalFloorsCount, totalFloorsCount, 100);
  }


  hideGlbMapOverlay(restoreWireframe = true): void {
    this.unbindCesiumCameraSync();
    this.clearGlbMapAttachedModels();
    this.clearGlbMapFloorHighlight();
    this.disposeGlbMapHighlightMaterialCache();
    this.glbMapFloorCatalog.clear();
    this.campusGlbBuildingMap.clear();
    const scene = this.h().getThreeScene();
    if (this.glbMapRootGroup && scene) {
      scene.remove(this.glbMapRootGroup);
      this.disposeObject3D(this.glbMapRootGroup);
      this.glbMapRootGroup = null;
    }
    if (restoreWireframe && this.h().getBuildingContainerGroup()) {
      this.h().getBuildingContainerGroup()!.visible = true;
      this.h().getBuildingModels()?.forEach((group) => {
        group.visible = true;
      });
    }
    const controls = this.h().getControls();
    if (restoreWireframe && controls) {
      controls.enabled = true;
    }
    this.glbMapOverlayActive = false;
  }

  private renderMapOverlayFrame(syncCamera = true): void {
    const renderer = this.h().getThreeRenderer();
    const scene = this.h().getThreeScene();
    const camera = this.h().getThreeCamera();
    if (!this.glbMapOverlayActive || !renderer || !scene || !camera) {
      return;
    }
    if (syncCamera) {
      this.h().resizeThreeJSRenderer();
      this.h().synchronizeCameras();
    }
    renderer.render(scene, camera);
    this.coresService.getViewer()?.scene.requestRender();
  }

  /** Force one GLB overlay render after external camera changes (e.g. init view restore). */
  renderGlbMapOverlayFrame(): void {
    this.renderMapOverlayFrame(true);
  }

  private getGlbMapPointerRect(): DOMRect | null {
    const el = this.h().getThreeRenderer()?.domElement ?? this.h().getCesiumThreeContainer();
    if (!el) {
      return null;
    }
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return null;
    }
    return rect;
  }

  private setMouseFromEvent(event: MouseEvent, rect: DOMRect): void {
    this.h().getMouse().x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.h().getMouse().y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private raycastGlbMapObjects(): THREE.Intersection[] {
    const camera = this.h().getThreeCamera();
    const scene = this.h().getThreeScene();
    if (!camera || !scene) {
      return [];
    }
    const glbRoots = this.getGlbMapModelRoots();
    if (!glbRoots.length) {
      return [];
    }
    this.h().getRaycaster().setFromCamera(this.h().getMouse(), camera);
    return this.h().getRaycaster().intersectObjects(glbRoots, true);
  }

  private bindCesiumCameraSync(): void {
    const viewer = this.coresService.getViewer();
    if (!viewer || this.cameraChangedListener) {
      return;
    }
    // requestRenderMode: camera.changed drives sync; postRender loop is in startRenderLoop().
    this.cameraChangedListener = () => this.renderMapOverlayFrame();
    viewer.camera.changed.addEventListener(this.cameraChangedListener);
  }

  private unbindCesiumCameraSync(): void {
    const viewer = this.coresService.getViewer();
    if (!viewer || !this.cameraChangedListener) {
      return;
    }
    viewer.camera.changed.removeEventListener(this.cameraChangedListener);
    this.cameraChangedListener = undefined;
  }

  private static readonly HIDE_INTERIOR_ASSETS_ON_MAP = true;

  /**
   * Dynamically checks if a mesh is a non-structural interior asset inside a room.
   * Keeps building structural elements (Floors, Slabs, Walls, Windows, Roofs, Facades, Bands)
   * while filtering out room contents and asset objects dynamically without hardcoded keyword arrays.
   */
  private isInteriorRoomAsset(child: THREE.Object3D): boolean {
    // 1. Check GLTF Extras / Metadata tags from node hierarchy
    const extras = this.getGltfExtrasFromNodeChain(child);
    if (
      extras['asset_id'] != null ||
      extras['assetId'] != null ||
      extras['is_asset'] === true ||
      extras['isAsset'] === true ||
      extras['is_interior'] === true
    ) {
      return true;
    }

    const category = String(extras['category'] || extras['type'] || extras['asset_type'] || '').toLowerCase();
    if (category === 'asset' || category === 'furniture' || category === 'prop' || category === 'interior_asset' || category === 'decor') {
      return true;
    }

    // 2. Structural Mesh Check (Floor plates, Outer Walls, Glazing, Roof, Shell, Room boundary)
    if (this.isGlbBuildingStructureMesh(child)) {
      return false;
    }

    // 3. Node Hierarchy Check: If nested inside a Room or Floor container, but not structural -> it's room interior content!
    const roomParent = this.getGlbRoomOrFloorParentNode(child);
    if (roomParent) {
      const roomType = String(roomParent.userData?.['room_type'] || roomParent.userData?.['type'] || '').toLowerCase();
      if (roomType === 'asset' || roomType === 'interior') {
        return true;
      }
      return true;
    }

    return false;
  }

  private isGlbBuildingStructureMesh(child: THREE.Object3D): boolean {
    const name = String(child.name || '').toLowerCase();
    const parentName = String(child.parent?.name || '').toLowerCase();

    let matNames = '';
    let isTransparentMat = false;
    if (child instanceof THREE.Mesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      matNames = mats.map((m) => String(m.name || '')).join(' ').toLowerCase();
      isTransparentMat = mats.some((m) => m.transparent === true || (typeof m.opacity === 'number' && m.opacity < 0.99));
    }

    const combined = `${name} ${parentName} ${matNames}`;
    const extras = this.getGltfExtrasFromNodeChain(child);
    const roomType = String(extras['room_type'] || extras['roomType'] || '').toLowerCase();

    if (
      roomType === 'floor_band' ||
      roomType === 'wall' ||
      roomType === 'slab' ||
      roomType === 'facade' ||
      roomType === 'structure' ||
      roomType === 'window' ||
      roomType === 'glazing'
    ) {
      return true;
    }

    // Window/Glass materials or structural transparent meshes are ALWAYS building structure!
    if (isTransparentMat) {
      return true;
    }

    const structureKeywords = [
      'floor', 'wall', 'slab', 'roof', 'window', 'win_', 'win', 'glass', 'glaz',
      'facade', 'shell', 'structure', 'curtain', 'opening', 'frame', 'plate', 'panel',
      'building', 'hangar', 'terminal', 'tower'
    ];

    if (this.isGlbFloorPlateMeshName(child.name) || structureKeywords.some((k) => combined.includes(k))) {
      return true;
    }

    return false;
  }

  private getGlbRoomOrFloorParentNode(object: THREE.Object3D): THREE.Object3D | null {
    let node: THREE.Object3D | null = object.parent;
    while (node) {
      const name = String(node.name || '');
      const userData = node.userData || {};
      if (
        name.startsWith('Floor_') ||
        name.startsWith('Room_') ||
        userData['room_id'] != null ||
        userData['roomId'] != null ||
        userData['floor_id'] != null ||
        userData['floorId'] != null
      ) {
        return node;
      }
      node = node.parent;
    }
    return null;
  }

  private prepareGlbMeshesForMap(root: THREE.Object3D): void {
    root.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }

      if (BlenderBuildingService.HIDE_INTERIOR_ASSETS_ON_MAP && this.isInteriorRoomAsset(child)) {
        child.visible = false;
        child.userData['isFilteredAsset'] = true;
        return;
      }

      child.frustumCulled = true;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        material.side = THREE.DoubleSide;
        if ('envMap' in material) {
          (material as any).envMap = null;
        }
        if ('envMapIntensity' in material) {
          (material as any).envMapIntensity = 0.0;
        }

        const name = (child.name + ' ' + (material.name || '')).toLowerCase();
        const isWindow =
          name.includes('window') ||
          name.includes('glass') ||
          name.includes('win_') ||
          name.includes('win') ||
          name.includes('glaz') ||
          name.includes('curtain') ||
          name.includes('pane') ||
          material.transparent === true ||
          (typeof material.opacity === 'number' && material.opacity < 0.99);

        if (isWindow) {
          if ('color' in material && (material as any).color) {
            (material as any).color.setHex(0x324c75);
          }
          if ('map' in material && (material as any).map) {
            (material as any).map = null;
          }
          if ('opacity' in material) {
            (material as any).opacity = 0.9;
          }
          if ('roughness' in material) {
            (material as any).roughness = 0.2;
          }
        } else {
          if ('color' in material && (material as any).color) {
            (material as any).color.setHex(0xffffff);
          }
          if ('roughness' in material) {
            (material as any).roughness = 0.4;
          }
          if ('metalness' in material) {
            (material as any).metalness = 0.0;
          }
        }
        material.needsUpdate = true;
      });
      const floorStackId = this.extractGlbFloorStackId(child);
      if (floorStackId) {
        child.userData['glbFloorStackId'] = floorStackId;
      }
    });
  }

  private getGltfExtrasFromNodeChain(object: THREE.Object3D, maxDepth = 20): Record<string, unknown> {
    let node: THREE.Object3D | null = object;
    for (let depth = 0; node && depth < maxDepth; depth++) {
      const userData = node.userData || {};
      if (userData['floor_id'] != null || userData['floorId'] != null || userData['room_id'] != null || userData['roomId'] != null) {
        return userData;
      }
      const nested = userData['extras'];
      if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
        return nested as Record<string, unknown>;
      }
      node = node.parent;
    }
    return {};
  }

  private isGlbFloorPlateMeshName(name: string): boolean {
    const upper = String(name || '').toUpperCase();
    return upper.includes('FLOOR_PLATE') || upper.includes('_PLATE');
  }

  private isGlbFloorLikePick(object: THREE.Object3D): boolean {
    let node: THREE.Object3D | null = object;
    for (let depth = 0; node && depth < 14; depth++) {
      const name = node.name || '';
      if (name.includes('Floor_')) {
        return true;
      }
      if (this.isGlbFloorPlateMeshName(name)) {
        return true;
      }
      const extras = this.getGltfExtrasFromNodeChain(node);
      const floorId = extras['floor_id'] ?? extras['floorId'];
      if (floorId != null && String(floorId).trim() !== '') {
        return true;
      }
      node = node.parent;
    }
    return false;
  }

  private extractGlbFloorStackId(object: THREE.Object3D): string {
    let parent: THREE.Object3D | null = object.parent;
    while (parent) {
      const name = parent.name || '';
      if (name.startsWith('Floor_')) {
        return name.replace(/^Floor_/i, '');
      }
      parent = parent.parent;
    }

    const extras = this.getGltfExtrasFromNodeChain(object);
    const fromExtras = extras['floor_id'] ?? extras['floorId'];
    if (fromExtras !== undefined && fromExtras !== null && String(fromExtras).trim() !== '') {
      return String(fromExtras).trim();
    }

    const roomId = ((object.name || '').split('/').pop() || '').replace(/^Room_/i, '');
    if (roomId.includes('_')) {
      return roomId.split('_')[0];
    }
    if (/^\d{6}$/.test(roomId)) {
      const parsed = parseInt(roomId.slice(0, 3), 10);
      return Number.isNaN(parsed) ? '1' : String(parsed);
    }
    if (roomId.length > 2) {
      return roomId.slice(0, -2);
    }
    return '';
  }

  private getGlbFloorParentNode(object: THREE.Object3D): THREE.Object3D | null {
    let node: THREE.Object3D | null = object;
    while (node) {
      if ((node.name || '').startsWith('Floor_')) {
        return node;
      }
      node = node.parent;
    }
    return null;
  }

  private getGlbFloorParentExtras(object: THREE.Object3D): Record<string, unknown> {
    const floorNode = this.getGlbFloorParentNode(object);
    if (!floorNode) {
      return {};
    }
    const userData = floorNode.userData || {};
    if (userData['extras'] && typeof userData['extras'] === 'object') {
      return userData['extras'] as Record<string, unknown>;
    }
    return userData;
  }

  private isGlbHighlightMesh(mesh: THREE.Mesh): boolean {
    const name = mesh.name || '';
    if (name.includes('_marker') || name === '__root__') {
      return false;
    }
    const extras = this.getGltfExtrasFromNodeChain(mesh);
    if (String(extras['room_type'] ?? extras['roomType'] ?? '').toLowerCase() === 'floor_band') {
      return false;
    }
    return this.isGlbFloorLikePick(mesh) || /^Room_/i.test(name) || this.isGlbFloorPlateMeshName(name);
  }

  private readGlbMeshFloorNumber(mesh: THREE.Object3D, stackKey: string): number | undefined {
    const floorExtras = this.getGlbFloorParentExtras(mesh);
    const fromFloorParent = this.getGlbFloorNumberHint(stackKey, floorExtras);
    if (fromFloorParent !== undefined) {
      return fromFloorParent;
    }
    const chainExtras = this.getGltfExtrasFromNodeChain(mesh);
    return this.getGlbFloorNumberHint(stackKey, chainExtras);
  }

  private readGlbFloorPerimeterId(mesh: THREE.Object3D): string | undefined {
    const extras = this.getGlbFloorParentExtras(mesh);
    const perimeter =
      extras['floor_perimeter_id'] ??
      extras['perimeterId'] ??
      extras['perimeter_id'] ??
      extras['floorPerimeterId'];
    if (perimeter === undefined || perimeter === null) {
      return undefined;
    }
    const trimmed = String(perimeter).trim();
    return trimmed || undefined;
  }

  private extractSortedGlbFloorGroups(scene: THREE.Object3D, buildingInfo?: any): GlbFloorGroup[] {
    scene.updateMatrixWorld(true);

    const floorMap = new Map<string, { floorNumber?: number; meshes: THREE.Mesh[]; minY: number; maxY: number }>();
    const unassignedMeshes: THREE.Mesh[] = [];

    const rawBox = new THREE.Box3().setFromObject(scene);
    const minH = rawBox.min.y;
    const maxH = rawBox.max.y;
    const totalH = Math.max(0.1, maxH - minH);

    scene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }

      const stackKey = this.extractGlbFloorStackId(child);
      const floorNumHint = this.readGlbMeshFloorNumber(child, stackKey);

      const mBox = new THREE.Box3();
      if (child.geometry) {
        child.geometry.computeBoundingBox();
        if (child.geometry.boundingBox) {
          mBox.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
        } else {
          mBox.setFromObject(child);
        }
      } else {
        mBox.setFromObject(child);
      }

      const mMinY = mBox.min.y;
      const mMaxY = mBox.max.y;

      if (stackKey) {
        let group = floorMap.get(stackKey);
        if (!group) {
          group = { floorNumber: floorNumHint, meshes: [], minY: mMinY, maxY: mMaxY };
          floorMap.set(stackKey, group);
        } else {
          group.minY = Math.min(group.minY, mMinY);
          group.maxY = Math.max(group.maxY, mMaxY);
          if (group.floorNumber == null && floorNumHint != null) {
            group.floorNumber = floorNumHint;
          }
        }
        group.meshes.push(child);
      } else {
        unassignedMeshes.push(child);
      }
    });

    const result: Array<{
      floorId: string;
      floorNumber: number;
      minY: number;
      maxY: number;
      meshes: THREE.Mesh[];
    }> = [];

    if (floorMap.size > 0) {
      floorMap.forEach((group, stackKey) => {
        const parsedNum = group.floorNumber ?? this.glbFloorSortKey(stackKey);
        result.push({
          floorId: stackKey,
          floorNumber: parsedNum !== 0 ? parsedNum : Math.round(((group.minY - minH) / totalH) * 10) + 1,
          minY: group.minY,
          maxY: group.maxY,
          meshes: group.meshes,
        });
      });

      if (unassignedMeshes.length > 0) {
        unassignedMeshes.forEach((mesh) => {
          const mBox = new THREE.Box3().setFromObject(mesh);
          const meshMinY = mBox.min.y;
          let bestGroup = result[0];
          let minDiff = Infinity;
          result.forEach((fg) => {
            const diff = Math.abs(fg.minY - meshMinY);
            if (diff < minDiff) {
              minDiff = diff;
              bestGroup = fg;
            }
          });
          if (bestGroup) {
            bestGroup.meshes.push(mesh);
          }
        });
      }
    } else {
      let numFloorsFromBuilding = 0;
      if (Array.isArray(buildingInfo?.floors) && buildingInfo.floors.length > 0) {
        let maxNum = 0;
        buildingInfo.floors.forEach((f: any) => {
          const num = Number(f?.number ?? f?.floorNumber ?? f?.level ?? 0);
          if (num > maxNum) {
            maxNum = num;
          }
        });
        numFloorsFromBuilding = Math.max(maxNum, buildingInfo.floors.length);
      }
      if (!numFloorsFromBuilding) {
        numFloorsFromBuilding = buildingInfo?.floorsCount || buildingInfo?.numberOfFloors || buildingInfo?.number;
      }
      const totalFloorsCount = numFloorsFromBuilding && numFloorsFromBuilding > 0
        ? Math.min(25, Math.max(1, numFloorsFromBuilding))
        : Math.max(1, Math.min(15, Math.round(totalH / 3.0)));

      const floorBands: Array<{
        floorId: string;
        floorNumber: number;
        minY: number;
        maxY: number;
        meshes: THREE.Mesh[];
      }> = [];
      for (let f = 1; f <= totalFloorsCount; f++) {
        const bandMinY = minH + ((f - 1) / totalFloorsCount) * totalH;
        const bandMaxY = minH + (f / totalFloorsCount) * totalH;
        floorBands.push({
          floorId: `Band_${f}`,
          floorNumber: f,
          minY: bandMinY,
          maxY: bandMaxY,
          meshes: [],
        });
      }

      scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mBox = new THREE.Box3();
          if (child.geometry) {
            child.geometry.computeBoundingBox();
            if (child.geometry.boundingBox) {
              mBox.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
            } else {
              mBox.setFromObject(child);
            }
          } else {
            mBox.setFromObject(child);
          }
          const mMinY = mBox.min.y;
          const bandIndex = Math.min(
            totalFloorsCount - 1,
            Math.max(0, Math.floor(((mMinY - minH) / totalH) * totalFloorsCount))
          );
          floorBands[bandIndex].meshes.push(child);
        }
      });

      floorBands.forEach((band) => {
        if (band.meshes.length > 0) {
          result.push(band);
        }
      });
    }

    // Sort floor groups STRICTLY by actual physical vertical Y-elevation from lowest Y (ground) to highest Y (roof)
    result.sort((a, b) => a.minY - b.minY);

    // Assign sequential floor numbers (1, 2, 3...) based on physical height order if floorNumber is missing/invalid
    result.forEach((group, idx) => {
      if (!group.floorNumber || group.floorNumber <= 0) {
        group.floorNumber = idx + 1;
      }
    });

    const buildingId = String(buildingInfo?.perimeterId ?? buildingInfo?.id ?? buildingInfo?.name ?? 'UnknownBuilding');
    let totalMeshCount = 0;
    scene.traverse((c) => { if (c instanceof THREE.Mesh) totalMeshCount++; });

    console.group(`[GLB Diagnostic Log] Perimeter / Building ID: ${buildingId}`);
    console.log(`- Total mesh count: ${totalMeshCount}`);
    console.log(`- Detected floorGroups count: ${result.length}`);
    result.forEach((group, idx) => {
      console.log(`  FloorGroup [${idx}]: floorId="${group.floorId}", floorNumber=${group.floorNumber}, minY=${group.minY.toFixed(2)}, maxY=${group.maxY.toFixed(2)}, meshesCount=${group.meshes.length}`);
      console.log(`    Meshes:`, group.meshes.slice(0, 10).map((m) => m.name || '(unnamed mesh)'));
    });
    console.groupEnd();

    return result;
  }

  private rebuildGlbMapFloorCatalog(): void {
    this.glbMapFloorCatalog.clear();
    for (const root of this.getGlbMapModelRoots()) {
      root.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) {
          return;
        }
        const stackKey = this.extractGlbFloorStackId(child);
        if (!stackKey) {
          return;
        }
        let entry = this.glbMapFloorCatalog.get(stackKey);
        if (!entry) {
          entry = { meshes: [] };
          this.glbMapFloorCatalog.set(stackKey, entry);
        }
        if (this.isGlbHighlightMesh(child) && !entry.meshes.includes(child)) {
          entry.meshes.push(child);
        }
        if (entry.floorNumber == null) {
          entry.floorNumber = this.readGlbMeshFloorNumber(child, stackKey);
        }
        if (!entry.perimeterId) {
          entry.perimeterId = this.readGlbFloorPerimeterId(child);
        }
        child.userData['glbFloorStackId'] = stackKey;
      });
    }
  }

  private resolveGlbMapHighlightStackKey(floorRef: string, resolvedFloor?: any): string {
    const raw = String(floorRef ?? '').trim();
    if (!raw) {
      return raw;
    }

    const idCandidates = this.uniqNonEmptyIds([
      raw,
      resolvedFloor?.perimeterId,
      resolvedFloor?.id,
      resolvedFloor?.arcAngelId,
    ]);

    for (const candidate of idCandidates) {
      if (this.glbMapFloorCatalog.has(candidate)) {
        return candidate;
      }
    }

    for (const [stackKey, entry] of this.glbMapFloorCatalog) {
      if (entry.perimeterId && idCandidates.includes(entry.perimeterId)) {
        return stackKey;
      }
    }

    const floorNumbers = new Set<number>();
    for (const candidate of idCandidates) {
      const parsed = this.extractFloorNumberFromArea(candidate);
      if (parsed != null) {
        floorNumbers.add(parsed);
      }
    }
    if (resolvedFloor) {
      if (resolvedFloor.number !== undefined && resolvedFloor.number !== null) {
        floorNumbers.add(Number(resolvedFloor.number));
      }
      if (resolvedFloor.floorNumber !== undefined && resolvedFloor.floorNumber !== null) {
        floorNumbers.add(Number(resolvedFloor.floorNumber));
      }
      if (resolvedFloor.level !== undefined && resolvedFloor.level !== null) {
        floorNumbers.add(Number(resolvedFloor.level));
      }
      const label = String(resolvedFloor.label || resolvedFloor.name || '');
      const labelMatch = label.match(/(?:FLOOR|LEVEL)\s*(\d+)/i);
      if (labelMatch) {
        floorNumbers.add(parseInt(labelMatch[1], 10));
      }
    }

    for (const floorNumber of floorNumbers) {
      const hits = [...this.glbMapFloorCatalog.entries()].filter(([, entry]) => entry.floorNumber === floorNumber);
      if (hits.length === 1) {
        return hits[0][0];
      }
    }

    for (const candidate of idCandidates) {
      const sortKey = this.glbFloorSortKey(candidate);
      if (sortKey === 0) {
        continue;
      }
      const hits = [...this.glbMapFloorCatalog.keys()].filter((key) => this.glbFloorSortKey(key) === sortKey);
      if (hits.length === 1) {
        return hits[0];
      }
    }

    return this.toBabylonViewerHighlightId(raw, resolvedFloor);
  }

  private getGlbMapFloorMeshes(stackKey: string): THREE.Mesh[] {
    const entry = this.glbMapFloorCatalog.get(stackKey);
    return entry?.meshes.length ? entry.meshes : [];
  }

  private rememberGlbMapOriginalMaterial(mesh: THREE.Mesh): THREE.Material | null {
    const current = mesh.material;
    if (!current || Array.isArray(current)) {
      return null;
    }
    if (!this.glbMapOriginalMaterials.has(mesh)) {
      this.glbMapOriginalMaterials.set(mesh, current);
    }
    return this.glbMapOriginalMaterials.get(mesh) ?? null;
  }

  private getOrCreateGlbHighlightMaterial(
    mesh: THREE.Mesh
  ): (THREE.MeshStandardMaterial | THREE.MeshPhongMaterial | THREE.MeshLambertMaterial) | null {
    const cached = this.glbMapHighlightMaterialCache.get(mesh);
    if (cached) {
      return cached as THREE.MeshStandardMaterial | THREE.MeshPhongMaterial | THREE.MeshLambertMaterial;
    }
    const original = this.rememberGlbMapOriginalMaterial(mesh);
    if (!original) {
      return null;
    }
    const highlight = original.clone();
    highlight.transparent = true;
    this.glbMapHighlightMaterialCache.set(mesh, highlight);
    return highlight as THREE.MeshStandardMaterial | THREE.MeshPhongMaterial | THREE.MeshLambertMaterial;
  }

  private disposeGlbMapHighlightMaterialCache(): void {
    this.glbMapHighlightMaterialCache.forEach((material) => {
      if (typeof material.dispose === 'function') {
        material.dispose();
      }
    });
    this.glbMapHighlightMaterialCache.clear();
    this.glbMapOriginalMaterials.clear();
  }

  private applyGlbMapHighlightMaterial(mesh: THREE.Mesh): void {
    const material = this.getOrCreateGlbHighlightMaterial(mesh);
    if (!material) {
      return;
    }
    if (!material.emissive) {
      material.emissive = new THREE.Color(0x000000);
    }
    material.emissive.setHex(BlenderBuildingService.GLB_FLOOR_HIGHLIGHT_EMISSIVE);
    if ('emissiveIntensity' in material) {
      material.emissiveIntensity = BlenderBuildingService.GLB_FLOOR_HIGHLIGHT_INTENSITY;
    }
    material.opacity = Math.min(1, (material.opacity ?? 0.4) + 0.35);
    material.transparent = true;
    material.needsUpdate = true;
    mesh.material = material;
  }

  private restoreGlbMapHighlightMaterial(mesh: THREE.Mesh): void {
    const original = this.glbMapOriginalMaterials.get(mesh);
    if (original) {
      mesh.material = original;
    }
  }

  private glbFloorStackKeysEquivalent(a: string, b: string): boolean {
    const left = String(a ?? '').trim();
    const right = String(b ?? '').trim();
    if (!left || !right) {
      return false;
    }
    if (left === right) {
      return true;
    }
    const leftNum = this.glbFloorSortKey(left);
    const rightNum = this.glbFloorSortKey(right);
    return leftNum !== 0 && leftNum === rightNum;
  }

  private glbFloorSortKey(floorId: string): number {
    const raw = String(floorId ?? '').trim();
    if (!raw) {
      return 0;
    }
    const upper = raw.toUpperCase();
    const tail = upper.startsWith('F') ? upper.slice(1) : upper;
    const match = tail.match(/^0*(\d+)$/);
    if (match) {
      return parseInt(match[1], 10);
    }
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private getGlbFloorNumberHint(floorId: string, extras: Record<string, unknown>): number | undefined {
    const fromExtras = extras['floorNumber'] ?? extras['floor_number'] ?? extras['number'] ?? extras['level'];
    if (fromExtras != null && String(fromExtras).trim() !== '') {
      const parsed = Number(String(fromExtras).trim());
      if (Number.isFinite(parsed) && parsed >= -200 && parsed <= 500) {
        return parsed;
      }
    }
    const fromDisplay = extras['displayFloorLevel'] ?? extras['display_floor_level'];
    if (fromDisplay != null && Number.isFinite(Number(fromDisplay))) {
      return Number(fromDisplay);
    }
    if (this.looksLikeOpaqueIntegrationId(floorId)) {
      return undefined;
    }
    if (/^\d+$/.test(floorId) && floorId.length >= 4) {
      const asNum = Number(floorId);
      if (asNum > 200) {
        return undefined;
      }
    }
    const sortKey = this.glbFloorSortKey(floorId);
    return sortKey !== 0 ? sortKey : undefined;
  }

  private resolveGlbMapFloorPick(object: THREE.Object3D): { floorId: string; floorNumberHint?: number } | null {
    const floorId = this.extractGlbFloorStackId(object);
    if (!floorId) {
      return null;
    }
    const floorExtras = this.getGlbFloorParentExtras(object);
    const chainExtras = this.getGltfExtrasFromNodeChain(object);
    const floorNumberHint =
      this.getGlbFloorNumberHint(floorId, floorExtras) ??
      this.getGlbFloorNumberHint(floorId, chainExtras);
    return { floorId, floorNumberHint };
  }

  /** GLB models live on glbMapRootGroup and/or wireframe-attached roots after upstream merge. */
  private getGlbMapModelRoots(): THREE.Object3D[] {
    const roots: THREE.Object3D[] = [];
    if (this.glbMapRootGroup) {
      roots.push(this.glbMapRootGroup);
    }
    for (const attached of this.glbMapAttachedModels) {
      if (attached && !roots.includes(attached)) {
        roots.push(attached);
      }
    }
    return roots;
  }

  private handleGlbMapFloorClick(floorId: string, floorNumberHint?: number): void {
    const resolved = this.resolveFloorAreaEntity(floorId);
    this.highlightGlbMapFloor(floorId, true, resolved);
    this.h().setPreviouslyHighlightedFloor(resolved?.id || resolved?.perimeterId || floorId);
    this.h().emitFloorClick(floorId, floorNumberHint);
  }

  private highlightGlbMapFloor(floorRef: string, force = false, resolvedFloor?: any): void {
    if (!BlenderBuildingService.FLOOR_HIGHLIGHT_ENABLED) {
      return;
    }
    const roots = this.getGlbMapModelRoots();
    const stackKey = this.resolveGlbMapHighlightStackKey(floorRef, resolvedFloor);
    if (!roots.length || !stackKey) {
      return;
    }
    if (
      !force &&
      this.glbMapHighlightedFloorId &&
      this.glbFloorStackKeysEquivalent(this.glbMapHighlightedFloorId, stackKey)
    ) {
      return;
    }
    this.clearGlbMapFloorHighlight();
    this.glbMapHighlightedFloorId = stackKey;

    for (const mesh of this.getGlbMapFloorMeshes(stackKey)) {
      this.rememberGlbMapOriginalMaterial(mesh);
      this.glbMapHighlightedMeshes.set(mesh, this.glbMapOriginalMaterials.get(mesh)!);
      this.applyGlbMapHighlightMaterial(mesh);
    }

    this.renderMapOverlayFrame(false);
  }

  private clearGlbMapFloorHighlight(): void {
    this.glbMapHighlightedMeshes.forEach((_originalMaterial, mesh) => {
      this.restoreGlbMapHighlightMaterial(mesh);
    });
    this.glbMapHighlightedMeshes.clear();
    this.glbMapHighlightedFloorId = null;
  }

  private findBuildingWireframeGroup(building: any): THREE.Group | null {
    const lookupKeys = [building?.id, building?.perimeterId]
      .filter((key) => key != null && key !== '')
      .map((key) => String(key));

    for (const key of lookupKeys) {
      if (this.h().getBuildingModels()?.has(key)) {
        return this.h().getBuildingModels().get(key) ?? null;
      }
    }

    if (this.h().getBuildingEntities() && this.h().getBuildingModels()) {
      for (const [entityId, group] of this.h().getBuildingModels().entries()) {
        const entity = this.h().getBuildingEntities().get(entityId);
        const entityKeys = [entity?.id, entity?.perimeterId]
          .filter((key) => key != null && key !== '')
          .map((key) => String(key));
        if (entityKeys.some((key) => lookupKeys.includes(key))) {
          return group;
        }
      }
    }

    return null;
  }

  private clearGlbMapAttachedModels(): void {
    this.glbMapHiddenNodes.forEach((node) => {
      node.visible = true;
    });
    this.glbMapHiddenNodes = [];
    this.glbMapAttachedModels.forEach((model) => {
      model.parent?.remove(model);
      this.disposeObject3D(model);
    });
    this.glbMapAttachedModels = [];
  }

  private hideWireframeMeshesForGlb(wireGroup: THREE.Group, glbModel: THREE.Object3D): void {
    wireGroup.traverse((child) => {
      if (child === glbModel || child === wireGroup || (glbModel && glbModel.getObjectById(child.id))) {
        return;
      }
      if (child.visible) {
        child.visible = false;
        this.glbMapHiddenNodes.push(child);
      }
    });
  }

  /** Wireframe bounds in wireGroup local space using mesh geometry (not world AABB). */
  private getWireframeLocalBoundingBox(
    wireGroup: THREE.Group,
    exclude: THREE.Object3D | null = null,
    floorFootprintOnly = true
  ): THREE.Box3 {
    const result = new THREE.Box3();
    const tempBox = new THREE.Box3();
    const relMatrix = new THREE.Matrix4();
    wireGroup.updateMatrixWorld(true);
    const wireGroupInv = new THREE.Matrix4().copy(wireGroup.matrixWorld).invert();

    const includeNode = (node: THREE.Object3D): boolean => {
      if (node === wireGroup || node === exclude) {
        return false;
      }
      if (this.glbMapAttachedModels.includes(node)) {
        return false;
      }
      if ((node as THREE.Sprite).isSprite || node.userData?.['type'] === 'building-marker') {
        return false;
      }
      if (floorFootprintOnly && node.userData?.['type'] !== 'floor') {
        return false;
      }
      return (node as THREE.Mesh).isMesh && !!(node as THREE.Mesh).geometry;
    };

    wireGroup.traverse((node) => {
      if (!includeNode(node)) {
        return;
      }

      const mesh = node as THREE.Mesh;
      mesh.geometry.computeBoundingBox();
      if (!mesh.geometry.boundingBox) {
        return;
      }

      relMatrix.multiplyMatrices(wireGroupInv, node.matrixWorld);
      tempBox.copy(mesh.geometry.boundingBox).applyMatrix4(relMatrix);
      result.union(tempBox);
    });

    if (floorFootprintOnly && result.isEmpty()) {
      return this.getWireframeLocalBoundingBox(wireGroup, exclude, false);
    }

    return result;
  }

  /** Lowest floor footprint only — full-stack bbox skews tall single-building scale/placement. */
  private getGroundFloorLocalBoundingBox(
    wireGroup: THREE.Group,
    exclude: THREE.Object3D | null = null
  ): THREE.Box3 {
    let minFloorNumber = Infinity;
    wireGroup.traverse((node) => {
      if (node.userData?.['type'] !== 'floor') {
        return;
      }
      const floorNumber = Number(node.userData['floorNumber'] ?? Infinity);
      if (floorNumber < minFloorNumber) {
        minFloorNumber = floorNumber;
      }
    });

    if (!Number.isFinite(minFloorNumber)) {
      return this.getWireframeLocalBoundingBox(wireGroup, exclude, true);
    }

    const result = new THREE.Box3();
    const tempBox = new THREE.Box3();
    const relMatrix = new THREE.Matrix4();
    wireGroup.updateMatrixWorld(true);
    const wireGroupInv = new THREE.Matrix4().copy(wireGroup.matrixWorld).invert();

    wireGroup.traverse((node) => {
      if (node === wireGroup || node === exclude) {
        return;
      }
      if (node.userData?.['type'] !== 'floor') {
        return;
      }
      if (Number(node.userData['floorNumber'] ?? Infinity) !== minFloorNumber) {
        return;
      }

      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) {
        return;
      }

      mesh.geometry.computeBoundingBox();
      if (!mesh.geometry.boundingBox) {
        return;
      }

      relMatrix.multiplyMatrices(wireGroupInv, node.matrixWorld);
      tempBox.copy(mesh.geometry.boundingBox).applyMatrix4(relMatrix);
      result.union(tempBox);
    });

    if (result.isEmpty()) {
      return this.getWireframeLocalBoundingBox(wireGroup, exclude, true);
    }

    return result;
  }

  private fitGlbModelForMap(
    model: THREE.Object3D,
    building: any,
    wireGroup: THREE.Group | null = this.findBuildingWireframeGroup(building),
    alignMode: 'footprint-center' | 'pin-origin' = 'footprint-center'
  ): THREE.Object3D {
    model.name = model.name || 'GlbMapModel';

    const box = new THREE.Box3().setFromObject(model);
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3());
      const useFootprintCenterAlign = alignMode === 'footprint-center';

      if (useFootprintCenterAlign) {
        model.position.x -= center.x;
        model.position.z -= center.z;
      }
      model.position.y -= box.min.y;

      const fitted = new THREE.Box3().setFromObject(model);
      const size = fitted.getSize(new THREE.Vector3());
      const horizontal = Math.max(size.x, size.z, 0.001);

      if (wireGroup) {
        const wireBox = useFootprintCenterAlign
          ? this.getWireframeLocalBoundingBox(wireGroup)
          : this.getGroundFloorLocalBoundingBox(wireGroup);
        const wireSize = wireBox.getSize(new THREE.Vector3());
        const wireHoriz = Math.max(wireSize.x, wireSize.z, 0.001);
        model.scale.setScalar(wireHoriz / horizontal);

        const wireCenter = wireBox.getCenter(new THREE.Vector3());
        const glbBox = new THREE.Box3().setFromObject(model);
        const glbCenter = glbBox.getCenter(new THREE.Vector3());

        if (useFootprintCenterAlign) {
          model.position.set(
            wireCenter.x - glbCenter.x,
            wireBox.min.y - glbBox.min.y,
            wireCenter.z - glbCenter.z
          );
        } else {
          // Pin-origin export: keep GLB local geometry; nudge by wire-vs-glb center delta only.
          model.position.set(
            wireCenter.x - glbCenter.x,
            wireBox.min.y - glbBox.min.y,
            wireCenter.z - glbCenter.z
          );
        }

        return model;
      }

      const glbHalfExtent = horizontal / 2;
      const footprintExtent = computeBuildingFootprintExtentMeters(
        building,
        this.coresService.getViewer()
      );
      if (footprintExtent > 0 && glbHalfExtent > 0.001) {
        const scale = Math.min(Math.max(footprintExtent / glbHalfExtent, 0.01), 500);
        model.scale.setScalar(scale);
      }
    }

    const wrapper = new THREE.Group();
    wrapper.add(model);
    const anchor = this.h().getBuildingGeographicCenter();
    const localCenter = resolveBuildingLocalCenter(building);
    if (anchor && localCenter) {
      const matrix = computeThreeBuildingMatrixFromAnchor(
        anchor,
        localCenter,
        this.coresService.getViewer()
      );
      wrapper.matrix.copy(matrix);
      wrapper.matrixAutoUpdate = false;
      wrapper.updateMatrixWorld(true);
    }

    return wrapper;
  }

  private disposeObject3D(root: THREE.Object3D): void {
    root.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        const material = child.material;
        if (Array.isArray(material)) {
          material.forEach((m) => m.dispose());
        } else {
          material?.dispose();
        }
      }
    });
  }
  highlightAlertFloors(floorIds: string[]): void {
    const normalized = Array.from(
      new Set(
        (floorIds || [])
          .map((id) => String(id || '').trim())
          .filter((id) => !!id)
      )
    ).map((id) => this.mapToBabylonFloorId(id));

    this.alertFloorHighlightSubject.next({ floorIds: normalized });
    this.alertHighlightedFloors = new Set(normalized);
  }

  highlightAlertRooms(roomIds: string[], buildingIds?: string[]): void {
    const normalized = Array.from(
      new Set(
        (roomIds || [])
          .map((id) => String(id || '').trim())
          .filter((id) => !!id)
      )
    );
    const normalizedBuildingIds = buildingIds?.length
      ? Array.from(new Set(buildingIds.map((id) => String(id || '').trim()).filter((id) => !!id)))
      : undefined;
    this.roomHighlightSubject.next({ roomIds: normalized, buildingIds: normalizedBuildingIds });
  }

  clearAlertFloorHighlights(): void {
    this.alertFloorHighlightSubject.next({ floorIds: [] });
    this.alertHighlightedFloors.clear();
    this.roomHighlightSubject.next({ roomIds: [] });
  }
  private extractFloorNumberFromArea(areaId: string): number | null {
    let areaEntity: any = this.h().getAreaMapEntities().get(areaId);
    if (areaEntity && String(areaEntity.perimeterType || '').toUpperCase() === 'ROOM') {
      areaEntity = undefined;
    }
    if (!areaEntity) {
      areaEntity = this.resolveFloorAreaEntity(areaId);
    }

    if (areaEntity) {
      if (typeof areaEntity.getFloorNumber === 'function') {
        const floorNum = areaEntity.getFloorNumber();
        if (floorNum) {
          return floorNum;
        }
      }

      if (areaEntity.level !== undefined) {
        return areaEntity.level;
      }

      const label = areaEntity.label || areaEntity.name || '';
      const floorMatch = label.match(/(?:FLOOR|LEVEL|Floor|Level)\s*(\d+)/i);
      if (floorMatch) {
        return parseInt(floorMatch[1], 10);
      }

      const numberMatch = label.match(/\d+/);
      if (numberMatch && parseInt(numberMatch[0], 10) <= 200) {
        return parseInt(numberMatch[0], 10);
      }
    }

    const idMatch = areaId.match(/\d+/);
    if (idMatch) {
      let num = parseInt(idMatch[0], 10);
      if (num > 200) {
        const areaIdMatch = areaId.match(/area-\d*?(\d)$/i);
        if (areaIdMatch) {
          const trailingFloor = parseInt(areaIdMatch[1], 10);
          if (trailingFloor > 0 && trailingFloor <= 200) {
            return trailingFloor;
          }
        }

        const lastTwo = num % 100;
        if (lastTwo > 0 && lastTwo <= 200) {
          return lastTwo;
        }
        const lastOne = num % 10;
        if (lastOne > 0 && lastOne <= 50) {
          return lastOne;
        }
      }
      return num;
    }

    return null;
  }

  private mapToBabylonFloorId(floorId: string): string {
    const floorNumber = this.extractFloorNumberFromArea(floorId);

    if (floorNumber !== null) {
      return floorNumber.toString();
    }

    const match = floorId.match(/\d+/);
    if (match) {
      let num = parseInt(match[0], 10);
      if (num > 200) {
        const possibleFloor = num % 100;
        if (possibleFloor > 0 && possibleFloor <= 200) {
          return possibleFloor.toString();
        }
      }
      return num.toString();
    }
    return floorId;
  }

  private uniqNonEmptyIds(values: unknown[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of values) {
      if (v === undefined || v === null) continue;
      const s = String(v).trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }

  /**
   * Resolves perimeter/floor entities when callers pass perimeterId, arcAngelId, UUID, or label —
   * `areaMapEntities` keys are floor.id but UI often passes perimeterId instead.
   */
  private resolveFloorAreaEntity(floorRef: string): any | undefined {
    const cand = String(floorRef ?? '').trim();
    if (!cand) return undefined;

    const isRoom = (e: any): boolean => String(e?.perimeterType || '').toUpperCase() === 'ROOM';

    const matchesIdentifiers = (e: any): boolean =>
      cand === String(e?.id ?? '') ||
      cand === String(e?.perimeterId ?? '') ||
      cand === String(e?.arcAngelId ?? '');

    let direct = this.h().getAreaMapEntities().get(cand);
    if (direct && !isRoom(direct)) {
      return direct;
    }

    for (const entity of this.h().getAreaMapEntities().values()) {
      if (isRoom(entity)) continue;
      if (matchesIdentifiers(entity)) return entity;
    }

    const needle = cand.toUpperCase();
    for (const entity of this.h().getAreaMapEntities().values()) {
      if (isRoom(entity)) continue;
      const label = String(entity?.label ?? entity?.name ?? '').trim().toUpperCase();
      if (label && needle === label) return entity;
    }
    return undefined;
  }

  /** GLB / API perimeter ids must not be reduced to stray digits via mapToBabylonFloorId. */
  private looksLikeOpaqueIntegrationId(s: string): boolean {
    const t = String(s ?? '').trim();
    if (!t) return false;
    if (/^[0-9a-f]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f]{12}$/i.test(t)) return true;
    return t.length >= 22;
  }

  /** Id passed through `floorHighlight$` into the Babylon GLB viewer (exact metadata floorId match preferred). */
  private toBabylonViewerHighlightId(floorRef: string, resolvedFloor?: any): string {
    const candidates = this.uniqNonEmptyIds([
      resolvedFloor?.perimeterId,
      resolvedFloor?.id,
      resolvedFloor?.arcAngelId,
      floorRef,
    ]);

    for (const c of candidates) {
      if (this.looksLikeOpaqueIntegrationId(c)) return c;
    }
    for (const c of candidates) {
      const m = this.mapToBabylonFloorId(c);
      if (m) return m;
    }
    return String(floorRef ?? '').trim();
  }

  private floorHighlightKeysEquivalent(a: string | null | undefined, b: string): boolean {
    if (!a) return false;
    if (a === b) return true;
    const ra = this.resolveFloorAreaEntity(String(a).trim());
    const rb = this.resolveFloorAreaEntity(String(b).trim());
    if (ra?.id && rb?.id && ra.id === rb.id) return true;
    return (
      this.toBabylonViewerHighlightId(String(a).trim(), ra) ===
      this.toBabylonViewerHighlightId(String(b).trim(), rb)
    );
  }

  /**
   * Setup Three.js scene lighting specifically for Blender GLB View (AFTER soft lighting setup).
   */
  setupBlenderLighting(
    scene: THREE.Scene | null,
    lightsGroup: THREE.Group
  ): void {
    if (!scene) return;

    if (!scene.children.includes(lightsGroup)) {
      scene.add(lightsGroup);
    }
    lightsGroup.clear();

    const ambientLight = new THREE.AmbientLight(0xffffff, 2.2);
    const sunLight = new THREE.DirectionalLight(0xffffff, 2.0);
    sunLight.position.set(100, 200, 100);
    sunLight.castShadow = false;

    const fillLight = new THREE.DirectionalLight(0xffffff, 1.2);
    fillLight.position.set(-80, 150, -80);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xd0d0d0, 1.0);

    lightsGroup.add(ambientLight, sunLight, fillLight, hemisphereLight);
  }
}
